import { ExportCsvButton } from '../../components/ui';
import { EXCLUSION_LABEL } from '../../constants/exclusions.js';
import { downloadCsv } from '../../utils/csv.js';
import { GapDetails } from './GapDetails.jsx';

// One record's line breakdown, built in the browser — the detail payload
// already carries every line, so there is nothing further to fetch.
// Mirrors core.hours.ROUNDING_TOLERANCE_HOURS (0.05h). Shown as minutes
// because "3 minutes" is the unit the billing team and the timeclock think in;
// if the backend setting is ever widened, update this label with it.
const ROUNDING_TOLERANCE_LABEL = '3 minutes';

const LINE_ITEM_COLUMNS = [
  { key: 'pay_category',  label: 'Pay Category' },
  { key: 'pay_code',      label: 'Pay Code' },
  { key: 'hours',         label: 'Hours' },
  { key: 'time_start',    label: 'Start' },
  { key: 'time_end',      label: 'End' },
  { key: 'price_level',   label: 'Price Level' },
  { key: 'rate',          label: 'Rate' },
  { key: 'amount',        label: 'Amount' },
  { key: 'is_excluded',   label: 'Excluded' },
];

// Where "Undo Handling" will send this record — the status it was marked FROM.
const PRIOR_STATUS_LABEL = {
  pending:         'Pending',
  gap:             'Gaps',
  source_deleted:  'Ops Review (source punch deleted)',
  source_modified: 'Ops Review (content drift)',
};

// Format a SQL DATETIME2 string ("2026-01-25T17:00:00" or "2026-01-25 17:00:00")
// to a compact "M/D HH:MM" suitable for the line-items table. When the line's
// end falls on a different date than its start (overnight shifts), include the
// end's date too. Returns "-" if no time info available.
function formatLineWindow(startStr, endStr) {
  if (!startStr && !endStr) return '-';
  const s = startStr ? new Date(startStr.replace(' ', 'T')) : null;
  const e = endStr   ? new Date(endStr.replace(' ', 'T'))   : null;
  if (!s || Number.isNaN(s.getTime())) return '-';
  const f = (d) => `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (!e || Number.isNaN(e.getTime())) return f(s);
  const sameDate = s.getFullYear() === e.getFullYear()
    && s.getMonth() === e.getMonth()
    && s.getDate() === e.getDate();
  // Same date: "1/25 05:00 → 17:00". Overnight: "1/25 17:00 → 1/26 05:00".
  const endStr2 = sameDate
    ? `${String(e.getHours()).padStart(2, '0')}:${String(e.getMinutes()).padStart(2, '0')}`
    : f(e);
  return `${f(s)} → ${endStr2}`;
}

// A value the record could not map to NetSuite, shown from the Dayforce source
// row instead. The record NULLs these columns whenever a mapping fails, which
// left the panel showing a dash on exactly the records ops needs detail for —
// so the unmapped Dayforce value is the honest fill, clearly marked as coming
// from the other side rather than from NetSuite.
function Unmapped({ value, hint }) {
  if (!value) return <span style={{ color: '#999' }}>-</span>;
  return (
    <span>
      <span style={{ color: '#8a4a00' }}>{value}</span>
      <span
        style={{ marginLeft: 6, fontSize: 10, color: '#8a4a00', background: '#fff0cc',
                 border: '1px solid #e6ba59', borderRadius: 3, padding: '0 4px' }}
        title={hint || 'Straight from the Dayforce timesheet entry — this value never '
                     + 'matched a NetSuite record, so nothing downstream of it could map.'}
      >
        Dayforce · unmapped
      </span>
    </span>
  );
}

// NS value when we have it, otherwise the Dayforce counterpart, otherwise "-".
function Mapped({ value, fallback, hint }) {
  if (value) return <span>{value}</span>;
  return <Unmapped value={fallback} hint={hint} />;
}

// The record's detail panel, with no opinion about where it is rendered.
// The Records table wraps it in a table row; /records/<id> renders it as a
// whole page. Both have to show the same thing, so there is one copy.
export function RecordDetailBody({ detail }) {
  const d = detail;
  const df = d.dayforce_context || {};
  return (
    <>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 13,
        }}>
          <div><strong>Employee ID:</strong> {d.employee_xref_code}</div>
          <div><strong>NS Employee ID:</strong>{' '}
            {d.ns_employee_id
              || <span style={{ color: '#8a4a00' }}>not in NetSuite</span>}
          </div>
          {/* Dayforce pay group — employee-level, so it reflects the group the
              employee is in NOW, not necessarily on the business date. */}
          <div><strong>Pay Group:</strong>{' '}
            {d.pay_group
              ? <span title={d.pay_group_xref_code || ''}>
                  {d.pay_group}
                  {d.pay_group_name ? ` — ${d.pay_group_name}` : ''}
                </span>
              : '-'}
          </div>
          <div><strong>Time:</strong> {d.time_start} - {d.time_end}</div>
          <div><strong>Location:</strong>{' '}
            <Mapped
              value={d.ns_location_name && `${d.ns_location_name} (${d.site_code})`}
              fallback={df.location_name
                ? `${df.location_name} (${d.site_code || df.location_xref})`
                : (d.site_code || df.location_xref)}
            />
          </div>
          <div><strong>Billing Title:</strong>{' '}
            <Mapped
              value={d.billing_title_name}
              fallback={df.derived_title}
              hint="Read from the Dayforce position/job — no NetSuite Billing Title carries this name."
            />
          </div>
          {/* Dayforce's own position/job string — the input to the billing
              title, and the thing to correct when the title won't map. */}
          <div><strong>Position / Job:</strong>{' '}
            <Mapped
              value={d.position_short_name}
              fallback={df.position_short_name || df.job_short_name || df.job_xref}
            />
          </div>
          <div><strong>Service Item:</strong> {d.service_item_name || <span style={{ color: '#999' }}>-</span>}</div>
          <div><strong>Cost Center:</strong>{' '}
            <Mapped value={d.cost_center_name} fallback={df.labor_task_ledger} />
          </div>
          <div><strong>Area:</strong> {d.area || <span style={{ color: '#999' }}>-</span>}</div>
          <div><strong>Task ID:</strong> {d.billing_task_id || <span style={{ color: '#999' }}>-</span>}</div>
          {/* The Dayforce side of Task ID / Cost Center / Area — all three come
              from this one code via the Record 419 mapping, so when they are
              blank this is what the engine had to work with. */}
          <div><strong>Labor Task:</strong>{' '}
            {df.labor_task
              ? (
                <span title={df.labor_task_name || ''}>
                  {d.billing_task_id
                    ? df.labor_task
                    : <Unmapped value={df.labor_task} />}
                  {df.labor_task_name ? ` — ${df.labor_task_name}` : ''}
                </span>
              )
              : <span style={{ color: '#999' }}>none on the Dayforce entry</span>}
          </div>
          <div><strong>Total Worked This Week:</strong>{' '}
            {d.total_worked_week != null ? `${d.total_worked_week}h` : '—'}
            {/* Marked from the WEEK's delta, not this record's — the
                correction sits on one day and every other day in the week
                shows the same rounded total. */}
            {d.week_rounding_delta != null
             && Number(d.week_rounding_delta) !== 0 ? (
              <span style={{
                marginLeft: 6, display: 'inline-block', padding: '1px 5px',
                borderRadius: 3, fontSize: 10, fontWeight: 600,
                background: '#eef6ec', color: '#2f6b2a',
              }}>
                rounded {Number(d.week_rounding_delta) > 0 ? '+' : '−'}
                {Math.abs(Number(d.week_rounding_delta)).toFixed(3)}
              </span>
            ) : null}
          </div>
          <div><strong>Weekly ST Hours:</strong>{' '}
            {d.weekly_hours != null ? `${d.weekly_hours}h` : '—'}{' '}
            <span style={{ color: '#888', fontSize: 11 }}>(for OT threshold check)</span>
          </div>
          <div><strong>Site OT Threshold:</strong>{' '}
            {d.site_ot_threshold != null ? `${d.site_ot_threshold}h` : '—'}
          </div>
          <div><strong>Customer OT Triggered:</strong>{' '}
            {d.is_customer_ot
              ? <span style={{ color: '#FF6600', fontWeight: 600 }}>Yes</span>
              : 'No'}
          </div>
          <div><strong>Week End:</strong> {d.week_end_date}</div>
        </div>

        {/* Near-whole week rounding — this record carried its week's correction. */}
        {d.hours_rounding_delta != null && Number(d.hours_rounding_delta) !== 0 ? (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#f2f8f1', color: '#2f6b2a',
            border: '1px solid #d5e7d2', borderRadius: 4, fontSize: 13,
          }}>
            <strong>
              Week rounded to {Math.round(Number(d.total_worked_week))}h — this
              record carries the {Number(d.hours_rounding_delta) > 0 ? '+' : '−'}
              {Math.abs(Number(d.hours_rounding_delta)).toFixed(3)}h adjustment
            </strong>
            <div style={{ marginTop: 4 }}>
              {/* Only the delta is exact (DECIMAL(8,4)); the whole-hour target
                  comes from rounding the post-correction week total, which is
                  what the rule aimed at by construction. Deliberately does NOT
                  reconstruct a per-day "Dayforce sent" figure: gross_hours is
                  the punch/adjustment total rather than the pay-summary
                  quantity the delta was applied to, and weekly_hours is the
                  TAB07 ADJUSTED total while the rounding targets QUALIFIED —
                  both give a wrong number on some records. */}
              Dayforce recorded this employee&apos;s week at this site as{' '}
              <strong>
                {Math.abs(Number(d.hours_rounding_delta)).toFixed(3)}h{' '}
                {Number(d.hours_rounding_delta) > 0 ? 'short of' : 'over'}
              </strong>{' '}
              a whole hour, because the timeclock counts whole minutes — a
              shift clocked out at 16:59 arrives as 4.983h, not 4.98h. Weeks
              within {ROUNDING_TOLERANCE_LABEL} of a whole hour are billed at
              that whole hour, and the entire adjustment is applied to one
              line rather than spread across the week — this record&apos;s.
            </div>
          </div>
        ) : null}

        {/* Weekly minute balance — a minute shifted so the invoice adds up.
            Deliberately separate from the rounding callout above: that one
            says the week's HOURS changed, this one says they did not. */}
        {d.minutes_moved != null ? (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#f4f7fd', color: '#2c4a7c',
            border: '1px solid #d6e0f5', borderRadius: 4, fontSize: 13,
          }}>
            <strong>
              Minute balance — this record&apos;s line{' '}
              {Number(d.minutes_moved) > 0
                ? `gained ${Math.abs(Number(d.minutes_moved))} minute${Math.abs(Number(d.minutes_moved)) === 1 ? '' : 's'}`
                : Number(d.minutes_moved) < 0
                  ? `gave up ${Math.abs(Number(d.minutes_moved))} minute${Math.abs(Number(d.minutes_moved)) === 1 ? '' : 's'}`
                  : 'gave and took a minute on different rate levels'}
            </strong>
            <div style={{ marginTop: 4 }}>
              A NetSuite TimeBill&apos;s hours are a duration in whole minutes, and
              NetSuite bills each day at two decimals. One hundredth of an hour
              is 0.6 of a minute, so a day whose minutes are not a multiple of
              three displays a third of a cent off — and a week of them can put
              the invoice&apos;s weekly column on 40.01 or 39.99 while the week
              itself is exactly 40:00. A minute was moved between two days of
              this employee&apos;s week at this rate level so the column adds up.
            </div>
            <div style={{ marginTop: 6, color: '#5a6b8a', fontSize: 12 }}>
              The week&apos;s total hours and its straight-time / overtime split are
              unchanged — only which day owns the minute is. This day is now
              one minute away from the punch Dayforce recorded.
            </div>
          </div>
        ) : null}

        {/* Canada mirror location — loaned employee at a 9xxxx site. */}
        {d.is_loaned_employee ? (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#f4f7fd', color: '#29417a',
            border: '1px solid #d6e0f5', borderRadius: 4, fontSize: 13,
          }}>
            <strong>Loaned employee — Canada mirror location</strong>
            <div style={{ marginTop: 4 }}>
              This employee&apos;s NetSuite home location is outside Canada, so their
              time at {d.site_code} bills through the site&apos;s mirror location.
            </div>
            <div style={{ marginTop: 6 }}>
              {d.mirror_location_id ? (
                <>
                  <strong>Billed to:</strong> {d.mirror_location_name}{' '}
                  <span style={{ color: '#888', fontSize: 11 }}>
                    (NS ID {d.mirror_location_id}) — instead of {d.ns_location_name}
                  </span>
                </>
              ) : (
                <span style={{ color: '#b25b00', fontWeight: 500 }}>
                  ⚠ No usable mirror location in NetSuite for {d.site_code} — this
                  record bills to the site&apos;s own location. See Rules ▸ Mirror
                  Locations.
                </span>
              )}
            </div>
            <div style={{ marginTop: 6, color: '#888', fontSize: 11 }}>
              Only the NetSuite location changes — rate, service item, cost center
              and project all come from the site worked.
            </div>
          </div>
        ) : null}

        {/* Manually Handled — who took it out of the queue, when, and why.
            record_state is the persistent table, so this survives every
            reprocess wipe along with the status itself. */}
        {d.status === 'manually_handled' ? (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#eef8f6', color: '#1c5d54',
            border: '1px solid #b9ddd6', borderRadius: 4, fontSize: 13,
          }}>
            <strong>Manually handled — billed outside this app</strong>
            <div style={{ marginTop: 4 }}>
              No reprocess, drift check or Ops Review pass will move this record
              again. Use <em>Undo Handling</em> on the Manually Handled tab to
              send it back to{' '}
              {PRIOR_STATUS_LABEL[d.record_state?.prior_status] || 'where it came from'}.
            </div>
            {d.record_state ? (
              <div style={{ marginTop: 6, color: '#41706a', fontSize: 12 }}>
                Marked by {d.record_state.last_status_by || 'unknown'}
                {d.record_state.last_status_change
                  ? ` on ${String(d.record_state.last_status_change).slice(0, 16).replace('T', ' ')}`
                  : ''}
                {d.record_state.prior_status
                  ? ` — was ${PRIOR_STATUS_LABEL[d.record_state.prior_status]
                       || d.record_state.prior_status}`
                  : ''}
                {d.record_state.notes ? ` — “${d.record_state.notes}”` : ''}
              </div>
            ) : null}
          </div>
        ) : null}

        {d.rates && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <strong>Contract Rates:</strong>{' '}
            ST=${d.rates.std_rate ?? '—'} | OT=${d.rates.ot_rate ?? '—'}{' '}
            | Hol=${d.rates.hol_rate ?? '—'} | Prem=${d.rates.prem_rate ?? '—'}{' '}
            (eff: {d.rates.effective_date || '—'})
          </div>
        )}

        {d.line_items && d.line_items.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <strong>Line Items:</strong>
              <ExportCsvButton
                onExport={() => downloadCsv(
                  `line-items-${d.punch_xref_code || d.id}`, d.line_items, LINE_ITEM_COLUMNS,
                )}
                label="Export lines"
                title="Download this record's line items as CSV"
              />
            </div>
            <table style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Pay Category</th><th>Pay Code</th><th>Hours</th>
                  <th>When</th>
                  <th>Price Level</th><th>Rate</th><th>Amount</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.line_items.map((li, i) => (
                  <tr key={i}>
                    <td>{li.pay_category}</td>
                    <td>{li.pay_code}</td>
                    <td>{li.hours}h</td>
                    <td style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                      {formatLineWindow(li.time_start, li.time_end)}
                    </td>
                    <td>{li.price_level || '-'}</td>
                    <td>{li.rate != null ? `$${li.rate}` : '-'}</td>
                    <td>{li.amount != null ? `$${li.amount}` : '-'}</td>
                    <td>
                      {li.is_excluded
                        ? <span className="badge remove">excluded</span>
                        : <span className="badge bill">billable</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <GapDetails gaps={d.mapping_gaps} details={d.gap_details} />

        {/* Manual exclusions are an ops DECISION, not a reference-data
            outcome, so they get their own panel with who/when/why — the same
            audit trail Manually Handled carries. */}
        {d.exclusion_reason === 'manually_excluded' ? (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#eef8f6', color: '#1c5d54',
            border: '1px solid #b9ddd6', borderRadius: 4, fontSize: 13,
          }}>
            <strong>Manually excluded — ops decided this is not billable</strong>
            <div style={{ marginTop: 4 }}>
              Not a mapping failure and not a reference-data rule. No reprocess
              will bring it back. Use <em>Undo Exclusion</em> on the Excluded tab
              to return it to{' '}
              {PRIOR_STATUS_LABEL[d.record_state?.prior_status] || 'where it came from'}.
            </div>
            {d.record_state ? (
              <div style={{ marginTop: 6, color: '#41706a', fontSize: 12 }}>
                Excluded by {d.record_state.last_status_by || 'unknown'}
                {d.record_state.last_status_change
                  ? ` on ${String(d.record_state.last_status_change).slice(0, 16).replace('T', ' ')}`
                  : ''}
                {d.record_state.prior_status
                  ? ` — was ${PRIOR_STATUS_LABEL[d.record_state.prior_status]
                       || d.record_state.prior_status}`
                  : ''}
                {d.record_state.notes ? ` — “${d.record_state.notes}”` : ''}
              </div>
            ) : null}
          </div>
        ) : d.exclusion_reason ? (
          <div style={{ marginTop: 12, color: '#3c3f7c', fontSize: 13 }}>
            <strong>Excluded by reference data:</strong>{' '}
            {EXCLUSION_LABEL[d.exclusion_reason] || d.exclusion_reason}
            <span style={{ color: '#888', marginLeft: 8, fontSize: 11 }}>
              (intentional skip — not a mapping failure)
            </span>
          </div>
        ) : null}

        {d.status === 'failed' && d.latest_push_error && (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#fdecea', color: '#7a1f12',
            border: '1px solid #f5c2c7', borderRadius: 4, fontSize: 13,
          }}>
            <strong>NetSuite sync error:</strong>
            <pre style={{
              margin: '6px 0 0', fontSize: 12, fontFamily: 'monospace',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {d.latest_push_error.error_message || d.latest_push_error}
            </pre>
            {d.latest_push_error.pushed_at && (
              <div style={{ marginTop: 4, color: '#888', fontSize: 11 }}>
                Last attempt: {d.latest_push_error.pushed_at}
              </div>
            )}
          </div>
        )}

        {d.ns_timebills && d.ns_timebills.length > 0 && (
          <div style={{ marginTop: 12, fontSize: 13 }}>
            <strong>Active NetSuite TimeBills:</strong>{' '}
            {d.ns_timebills.map((t) => (
              <span key={t.id} style={{
                display: 'inline-block', marginRight: 8, padding: '2px 8px',
                background: '#cce5ff', color: '#004085', borderRadius: 4,
                fontSize: 12,
              }}>
                NS {t.ns_timebill_id}
                {t.pay_category && ` · ${t.pay_category}`}
                {t.hours != null && ` · ${t.hours}h`}
              </span>
            ))}
          </div>
        )}

        {d.status === 'source_modified' && d.ns_timebills && d.ns_timebills.length > 0 && (
          <div style={{
            marginTop: 12, padding: '10px 12px',
            background: '#fff8e6', color: '#6e4c00',
            border: '1px solid #e6ba59', borderRadius: 4, fontSize: 13,
          }}>
            <strong>⚠ Content drift detected — synced to NetSuite vs current source data:</strong>
            <table style={{ marginTop: 8, fontSize: 12, width: '100%', background: '#fff' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: 4 }}>Pay Category</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Pay Code</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Hours</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Rate</th>
                  <th style={{ textAlign: 'right', padding: 4 }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: 4 }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {d.ns_timebills.map((t) => (
                  <tr key={`pushed-${t.id}`} style={{ background: '#cce5ff' }}>
                    <td style={{ padding: 4 }}>{t.pay_category || '-'}</td>
                    <td style={{ padding: 4 }}>{t.pay_code || '-'}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{t.hours}h</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>${t.rate}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>${t.amount}</td>
                    <td style={{ padding: 4 }}>
                      <strong>Synced to NS</strong> (TimeBill {t.ns_timebill_id})
                    </td>
                  </tr>
                ))}
                {(d.line_items || []).filter((li) => !li.is_excluded).map((li) => (
                  <tr key={`curr-${li.id}`} style={{ background: '#fff0cc' }}>
                    <td style={{ padding: 4 }}>{li.pay_category || '-'}</td>
                    <td style={{ padding: 4 }}>{li.pay_code || '-'}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>{li.hours}h</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>${li.rate}</td>
                    <td style={{ padding: 4, textAlign: 'right' }}>${li.amount}</td>
                    <td style={{ padding: 4 }}>
                      <strong>Current source</strong> (Dayforce/engine)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 6, fontSize: 11, color: '#888' }}>
              Use <em>Re-sync</em> to replace the NetSuite TimeBill(s) with the current source rows,
              or <em>Void</em> to delete the NS TimeBill(s) and leave the record as <em>pending</em> for re-review.
            </div>
          </div>
        )}
    </>
  );
}

export function RecordDetailRow({ detail, colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} style={{ background: '#f8f9fc', padding: 16 }}>
        <RecordDetailBody detail={detail} />
      </td>
    </tr>
  );
}
